/**
 * Componente AppointmentForm
 * Formulario para crear/editar citas CRM
 * Contacto es obligatorio (auditoría usabilidad #8).
 * @module components/appointments/AppointmentForm
 */

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { AppointmentCreate, AppointmentModality } from '@/types/appointment.types';
import type { CRMUser } from '@/types/crm';
import { BuildingOffice2Icon, VideoCameraIcon } from '@heroicons/react/24/outline';
import { sanitizeString, sanitizeXSS } from '@/utils/validators';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ContactSearchSelect } from '@/components/CRM/ContactSearchSelect';
import { useDelegations } from '@/hooks/useDelegations';
import { crmService } from '@/services/crmService';
import { adminService } from '@/services/adminService';
import { googleCalendarService } from '@/services/googleCalendarService';
import { useGoogleCalendarStatus } from '@/hooks/useGoogleCalendarStatus';

const appointmentSchema = z.object({
  scheduled_at: z.string().min(1, 'Fecha y hora requeridas'),
  duration: z
    .number()
    .min(15, 'Duración mínima 15 minutos')
    .max(480, 'Duración máxima 8 horas'),
  opportunity_id: z.string().optional(),
  timezone: z.string().min(1).default('Europe/Madrid'),
  agent_id: z.string().min(1, 'Debes seleccionar un agente'),
  contact_id: z.string().min(1, 'Debes seleccionar un contacto'),
  delegation_id: z.string().optional(),
  modality: z.enum(['in_person', 'video_call']).optional().nullable(),
  location: z.string().optional(),
  meeting_url: z.string().optional(),
  notes: z.string().optional(),
});

type AppointmentFormData = z.infer<typeof appointmentSchema> & { timezone: string };

interface AppointmentFormProps {
  onSubmit: (data: AppointmentCreate) => Promise<void>;
  onCancel: () => void;
  defaultValues?: Partial<AppointmentCreate>;
  opportunityId?: string;
  contactId?: string;
  agentId?: string;
}

export const AppointmentForm: React.FC<AppointmentFormProps> = ({
  onSubmit,
  onCancel,
  defaultValues,
  opportunityId,
  contactId,
  agentId,
}) => {
  const { activeDelegations } = useDelegations();
  const [users, setUsers] = useState<CRMUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const { status: gcalStatus } = useGoogleCalendarStatus();
  const [conflictWarning, setConflictWarning] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm<AppointmentFormData>({
    resolver: zodResolver(appointmentSchema) as any,
    defaultValues: {
      duration: 60,
      timezone: 'Europe/Madrid',
      opportunity_id: opportunityId || '',
      contact_id: contactId || '',
      agent_id: agentId || defaultValues?.agent_id || '',
      ...defaultValues,
    },
  });

  const watchedContactId = watch('contact_id');
  const watchedScheduledAt = watch('scheduled_at');
  const watchedDuration = watch('duration');
  const watchedAgentId = watch('agent_id');
  const watchedModality = watch('modality');
  const isFormValid =
    Boolean(watchedContactId?.trim()) &&
    Boolean(watchedScheduledAt?.trim()) &&
    Number(watchedDuration) >= 15 &&
    Boolean(watchedAgentId?.trim());

  // Cargar lista de agentes (responsables) para el select
  useEffect(() => {
    let cancelled = false;
    const loadUsers = async () => {
      try {
        const usersData = await crmService.getResponsibleUsers(true);
        if (!cancelled) {
          setUsers(usersData);
          const currentAgentId = agentId || defaultValues?.agent_id || '';
          if (!currentAgentId && usersData.length > 0) {
            const currentUser = adminService.getUser();
            if (currentUser?.id || currentUser?.email) {
              const currentEmail = currentUser.email?.toLowerCase();
              const currentCRMUser = usersData.find(
                (u) =>
                  u.id === currentUser.id ||
                  u.email === currentUser.email ||
                  (currentEmail && u.email?.toLowerCase() === currentEmail)
              );
              if (currentCRMUser) setValue('agent_id', currentCRMUser.id);
            }
          }
        }
      } catch (err) {
        if (!cancelled) console.error('Error cargando agentes:', err);
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    };
    loadUsers();
    return () => {
      cancelled = true;
    };
  }, [agentId, defaultValues?.agent_id, setValue]);

  // Pre-poblar campos cuando cambian las props
  useEffect(() => {
    if (opportunityId) setValue('opportunity_id', opportunityId);
    if (contactId) setValue('contact_id', contactId);
    if (agentId) setValue('agent_id', agentId);
  }, [opportunityId, contactId, agentId, setValue]);

  // Check Google Calendar availability when date/duration change
  useEffect(() => {
    if (!gcalStatus?.connected || !watchedScheduledAt || !watchedDuration) {
      setConflictWarning(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const start = new Date(watchedScheduledAt);
        if (isNaN(start.getTime())) return;
        const end = new Date(start.getTime() + watchedDuration * 60000);
        const result = await googleCalendarService.checkAvailability({
          start: start.toISOString(),
          end: end.toISOString(),
        });
        if (!result.is_available && result.busy_periods.length > 0) {
          setConflictWarning(
            `Hay ${result.busy_periods.length} evento(s) en Google Calendar en este horario`,
          );
        } else {
          setConflictWarning(null);
        }
      } catch {
        setConflictWarning(null);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [watchedScheduledAt, watchedDuration, gcalStatus?.connected]);

  const handleFormSubmit = async (data: AppointmentFormData) => {
    try {
      const payload: AppointmentCreate = {
        scheduled_at: data.scheduled_at,
        duration: data.duration,
        timezone: data.timezone || 'Europe/Madrid',
        agent_id: data.agent_id || undefined,
        contact_id: data.contact_id || undefined,
        delegation_id: data.delegation_id || undefined,
        modality: (data.modality as AppointmentModality) || undefined,
        location: data.location ? sanitizeString(sanitizeXSS(data.location.trim())) : undefined,
        meeting_url: data.meeting_url ? sanitizeString(sanitizeXSS(data.meeting_url.trim())) : undefined,
        notes: data.notes ? sanitizeString(sanitizeXSS(data.notes.trim())) : undefined,
      };
      if (data.opportunity_id?.trim()) {
        payload.opportunity_id = data.opportunity_id.trim();
      }
      // En desarrollo: ver payload enviado (agent_id debe ser UUID, no email)
      if (import.meta.env.DEV) {
        console.log('[AppointmentForm] Payload POST /crm/appointments:', JSON.stringify(payload, null, 2));
        const isUuid = (s: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
        if (payload.agent_id && !isUuid(payload.agent_id)) {
          console.warn('[AppointmentForm] agent_id no tiene formato UUID:', payload.agent_id, '- El backend espera UUID.');
        }
      }
      await onSubmit(payload);
    } catch (error) {
      console.error('Error al guardar cita:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      {/* Contacto (obligatorio) - Auditoría #8 */}
      <div>
        <ContactSearchSelect
          label="Contacto"
          required
          value={watchedContactId || ''}
          onChange={(contactId) => setValue('contact_id', contactId, { shouldValidate: true })}
          placeholder="Buscar contacto por nombre, email o teléfono..."
        />
        {errors.contact_id && (
          <p className="text-sm text-red-600 mt-1">{errors.contact_id.message}</p>
        )}
      </div>

      {/* Fecha y hora */}
      <div>
        <Label htmlFor="scheduled_at">Fecha y hora *</Label>
        <Input
          id="scheduled_at"
          type="datetime-local"
          {...register('scheduled_at')}
          className="w-full mt-1"
        />
        {errors.scheduled_at && (
          <p className="text-sm text-red-600 mt-1">
            {errors.scheduled_at.message}
          </p>
        )}
      </div>

      {/* Duración */}
      <div>
        <Label htmlFor="duration">Duración *</Label>
        <select
          id="duration"
          {...register('duration', { valueAsNumber: true })}
          className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value={15}>15 minutos</option>
          <option value={30}>30 minutos</option>
          <option value={45}>45 minutos</option>
          <option value={60}>1 hora</option>
          <option value={90}>1.5 horas</option>
          <option value={120}>2 horas</option>
          <option value={180}>3 horas</option>
          <option value={240}>4 horas</option>
        </select>
        {errors.duration && (
          <p className="text-sm text-red-600 mt-1">{errors.duration.message}</p>
        )}
      </div>

      {/* Google Calendar conflict warning */}
      {conflictWarning && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
          </svg>
          <p className="text-sm text-amber-800">{conflictWarning}</p>
        </div>
      )}

      {/* Agente entrevistador (obligatorio) */}
      <div>
        <Label htmlFor="agent_id">Agente entrevistador *</Label>
        <select
          id="agent_id"
          {...register('agent_id')}
          className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Seleccionar agente...</option>
          {loadingUsers ? (
            <option value="" disabled>Cargando...</option>
          ) : (
            users.map((user) => {
              const displayName = user.name?.trim() || user.email || 'Usuario sin nombre';
              return (
                <option key={user.id} value={user.id}>
                  {displayName}
                </option>
              );
            })
          )}
        </select>
      </div>

      {/* Delegación */}
      <div>
        <Label htmlFor="delegation_id">Delegación (opcional)</Label>
        <select
          id="delegation_id"
          {...register('delegation_id')}
          className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Sin delegación</option>
          {activeDelegations?.map((delegation) => (
            <option key={delegation.id} value={delegation.id}>
              {delegation.name} - {delegation.city}
            </option>
          ))}
        </select>
      </div>

      {/* Modalidad */}
      <div>
        <Label className="block mb-2">Modalidad (opcional)</Label>
        <div className="flex flex-wrap gap-4" role="group" aria-label="Modalidad de la cita">
          <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
            <input
              type="radio"
              {...register('modality')}
              value="in_person"
              className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
            />
            <BuildingOffice2Icon className="w-5 h-5 text-[#2E7D32]" aria-hidden />
            <span className="text-sm font-medium">Presencial</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
            <input
              type="radio"
              {...register('modality')}
              value="video_call"
              className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
            />
            <VideoCameraIcon className="w-5 h-5 text-[#1565C0]" aria-hidden />
            <span className="text-sm font-medium">Videollamada</span>
          </label>
        </div>
      </div>

      {/* Ubicación (presencial) o Enlace videollamada (videollamada) */}
      {watchedModality === 'video_call' ? (
        <div>
          <Label htmlFor="meeting_url">Enlace de videollamada (opcional)</Label>
          <Input
            id="meeting_url"
            type="url"
            {...register('meeting_url')}
            placeholder="https://meet.google.com/..."
            className="w-full mt-1"
            autoComplete="url"
          />
        </div>
      ) : (
        <div>
          <Label htmlFor="location">Ubicación (opcional)</Label>
          <Input
            id="location"
            type="text"
            {...register('location')}
            placeholder="Ej: Sala de reuniones 1, Oficina Salamanca"
            className="w-full mt-1"
          />
        </div>
      )}

      {/* Notas */}
      <div>
        <Label htmlFor="notes">Notas (opcional)</Label>
        <textarea
          id="notes"
          {...register('notes')}
          rows={3}
          placeholder="Notas adicionales sobre la cita..."
          className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
        />
      </div>

      {/* Oportunidad ID (opcional): se rellena si se abre el formulario desde una oportunidad */}
      <input type="hidden" {...register('opportunity_id')} />

      {/* Botones - Deshabilitado hasta que contacto, fecha, duración y agente sean válidos (#11) */}
      <div className="flex items-center gap-3 pt-4">
        <Button
          type="submit"
          disabled={!isFormValid || isSubmitting}
          className="flex-1 bg-blue-600 hover:bg-blue-700"
        >
          {isSubmitting ? 'Guardando...' : 'Guardar cita'}
        </Button>
        <Button
          type="button"
          onClick={onCancel}
          variant="outline"
          className="flex-1"
        >
          Cancelar
        </Button>
      </div>
    </form>
  );
};
